import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import hipaa from '../assets/hipaa.png';

const SecurityComplianceSection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ISO Badge Component
  const ISOBadge = ({ standard, title, color }) => (
    <div style={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: `3px solid ${color}`,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      minWidth: '140px',
      transition: 'all 0.3s ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-5px)';
      e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
    }}
    role="img"
    aria-label={`ISO ${standard} ${title} Certification`}
    >
      <div style={{
        fontSize: '2.5rem',
        fontWeight: '900',
        color: color,
        marginBottom: '8px',
        fontFamily: 'Arial, sans-serif',
      }}>
        ISO
      </div>
      <div style={{
        fontSize: '1.8rem',
        fontWeight: '700',
        color: color,
        marginBottom: '5px',
      }}>
        {standard}
      </div>
      <div style={{
        fontSize: '0.75rem',
        color: '#666',
        textAlign: 'center',
        lineHeight: '1.3',
        fontWeight: '500',
      }}>
        {title}
      </div>
    </div>
  );

  const safeguards = {
    administrative: [
      'NDAs with employees, clients, vendors, and visitors',
      'Role-based access controls and password management policies',
      'Ongoing security and HIPAA training programs',
      'Scheduled backups of all systems and documents'
    ],
    physical: [
      'Biometric entry control and restricted zone access',
      'CCTV monitoring in sensitive areas',
      'Secure server room with authorized entry only',
      'USB and removable media restrictions',
      'Automated screen lock and idle timeout security',
      'Strict "no personal devices" policy in designated work zones'
    ],
    technical: [
      'Encrypted data transmission over secure VPN tunnels',
      'Managed firewall access and continuous security monitoring',
      '3DES-level encryption aligned with HIPAA standards',
      'Enterprise-grade antivirus and automated threat updates',
      'Unique user IDs and controlled folder access',
      'Restricted internet access based on client policies',
      'Email controls with secured internal and external permissions'
    ]
  };

  // ✅ Organization Schema with Credentials
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "MDeRCM",
    "description": "HIPAA-compliant healthcare revenue cycle management services with ISO 9001 and ISO 27001 certifications",
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "certification",
        "name": "ISO 9001:2015 Quality Management Systems",
        "recognizedBy": {
          "@type": "Organization",
          "name": "International Organization for Standardization"
        }
      },
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "certification",
        "name": "ISO 27001:2013 Information Security Management",
        "recognizedBy": {
          "@type": "Organization",
          "name": "International Organization for Standardization"
        }
      },
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "certification",
        "name": "HIPAA Compliance Certification",
        "recognizedBy": {
          "@type": "Organization",
          "name": "U.S. Department of Health and Human Services"
        }
      }
    ]
  };

  // ✅ ImageObject Schema for HIPAA Badge
  const imageSchema = {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "contentUrl": "https://mdercm.com" + hipaa,
    "description": "HIPAA Compliant Medical Billing Outsourcing Services - MDeRCM Certification Badge",
    "name": "HIPAA Compliance Badge"
  };

  return (
    <section
      style={{
        backgroundColor: '#f8f9fa',
        padding: '80px 5%',
        fontFamily: 'Poppins, sans-serif',
      }}
      itemScope
      itemType="https://schema.org/Article"
      role="region"
      aria-label="Security and compliance certifications for healthcare RCM"
    >
      {/* ✅ Add Schemas to Head */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(imageSchema)}
        </script>
      </Helmet>

      {/* Main Heading */}
      <h2
        style={{
          fontSize: 'clamp(2rem, 5vw, 3rem)',
          fontWeight: '700',
          color: '#1A1A1A',
          marginBottom: '20px',
          textAlign: 'center',
          lineHeight: '1.2',
        }}
        itemProp="headline"
      >
        Quality & Compliance - HIPAA-Compliant Revenue Cycle Management Services
      </h2>

      <p style={{
        fontSize: '1.15rem',
        lineHeight: '1.8',
        color: '#555',
        maxWidth: '1000px',
        margin: '0 auto 50px',
        textAlign: 'center',
      }}
      itemProp="description"
      >
        At MDeRCM, quality and data security are foundational to our{' '}
        <Link 
          to="/why" 
          style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
          aria-label="Learn about our outsourced revenue cycle management services"
        >
          outsourced revenue cycle management services
        </Link>. We maintain industry certifications and proven best practices to deliver accuracy, consistency, and security for every client.
      </p>

      {/* Certification Badges Section */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '30px',
        marginBottom: '60px',
        flexWrap: 'wrap',
      }}
      role="list"
      aria-label="Compliance and security certifications"
      >
        {/* ISO 9001 Badge */}
        <div role="listitem">
          <ISOBadge 
            standard="9001" 
            title="Quality Management"
            color="#0077b6"
          />
        </div>

        {/* ISO 27001 Badge */}
        <div role="listitem">
          <ISOBadge 
            standard="27001" 
            title="Information Security"
            color="#2e7d32"
          />
        </div>

        {/* HIPAA Badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        role="listitem"
        >
          <img
            src={hipaa}
            alt="HIPAA Compliant Medical Billing Outsourcing Services - MDeRCM is fully certified for HIPAA compliance ensuring secure healthcare revenue cycle management"
            style={{
              maxWidth: '180px',
              height: 'auto',
              objectFit: 'contain',
              filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))',
            }}
            loading="lazy"
            itemProp="image"
          />
        </div>
      </div>

      {/* Main Content Grid */}
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
        }}
      >
        {/* ISO Certifications Description */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '40px',
          borderRadius: '12px',
          marginBottom: '40px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          <h3 style={{
            fontSize: '1.6rem',
            fontWeight: '700',
            color: '#0077b6',
            marginBottom: '20px',
          }}>
            ISO 9001 & ISO 27001 Certified
          </h3>
          <p style={{
            fontSize: '1.05rem',
            lineHeight: '1.8',
            color: '#444',
          }}>
            Our{' '}
            <Link 
              to="/why" 
              style={{ color: '#0077b6', textDecoration: 'underline' }}
            >
              medical billing outsourcing companies
            </Link>{' '}
            infrastructure maintains <strong>ISO 9001 certification for quality management</strong> and <strong>ISO 27001 certification for information security</strong>, ensuring every process is governed by proven best practices and audited controls. Our teams follow standardized workflows supported by measurable performance metrics to deliver accuracy, consistency, and timely results for every client. We are committed to continuous improvement and proactively enhance our systems to exceed client expectations and safeguard their information at every step.
          </p>
        </div>

        {/* HIPAA Compliance Section */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '40px',
          borderRadius: '12px',
          marginBottom: '40px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: '2px solid #4caf50',
        }}>
          <h3 style={{
            fontSize: '1.6rem',
            fontWeight: '700',
            color: '#2e7d32',
            marginBottom: '20px',
          }}>
            HIPAA Compliance
          </h3>
          <p style={{
            fontSize: '1.05rem',
            lineHeight: '1.8',
            color: '#444',
            marginBottom: '20px',
          }}>
            Protecting patient information is a responsibility we take seriously. MDeRCM is <strong>fully HIPAA compliant</strong>, and our teams are rigorously trained in privacy, security, and confidentiality protocols. We implement industry-leading safeguards to prevent unauthorized access and maintain strict adherence to federal healthcare data regulations.
          </p>
          <p style={{
            fontSize: '1.05rem',
            lineHeight: '1.8',
            color: '#444',
          }}>
            As one of the{' '}
            <Link 
              to="/why" 
              style={{ color: '#2e7d32', textDecoration: 'underline', fontWeight: '600' }}
            >
              best revenue cycle management companies
            </Link>, we ensure that all Protected Health Information (PHI) and electronic PHI (e-PHI) is handled with the highest level of security through our three-tier safeguard system: Administrative, Physical, and Technical controls.
          </p>
        </div>

        {/* Employee Security Section */}
        <div style={{
          backgroundColor: '#ffffff',
          padding: '40px',
          borderRadius: '12px',
          marginBottom: '40px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#0077b6',
            marginBottom: '20px',
          }}>
            Employee Screening & Access Control
          </h3>
          <p style={{
            fontSize: '1.05rem',
            lineHeight: '1.8',
            color: '#444',
          }}>
            Every employee undergoes an extensive screening and background verification process prior to onboarding. Once onboarded, system access is granted strictly on a role-based, need-to-know basis, supported by secure login credentials and usage monitoring. Employees sign confidentiality agreements and receive regular HIPAA and security training to reinforce data protection responsibilities.
          </p>
        </div>

        {/* Three Safeguards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '30px',
          marginBottom: '40px',
        }}>
          {/* Administrative Safeguards */}
          <div style={{
            backgroundColor: '#fff3e0',
            padding: '35px',
            borderRadius: '12px',
            border: '2px solid #ff9800',
          }}
          itemScope
          itemType="https://schema.org/ItemList"
          >
            <h4 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              color: '#e65100',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
            itemProp="name"
            >
              <span style={{ fontSize: '1.8rem' }} aria-hidden="true">📋</span>
              Administrative Safeguards
            </h4>
            <p style={{
              fontSize: '1rem',
              lineHeight: '1.7',
              color: '#555',
              marginBottom: '20px',
            }}>
              Comprehensive administrative protocols to protect electronic PHI (e-PHI):
            </p>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}>
              {safeguards.administrative.map((item, index) => (
                <li key={index} style={{
                  fontSize: '0.95rem',
                  lineHeight: '1.9',
                  color: '#444',
                  paddingLeft: '25px',
                  position: 'relative',
                  marginBottom: '10px',
                }}
                itemProp="itemListElement"
                >
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: '#ff9800',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                  }}
                  aria-hidden="true"
                  >✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Physical Safeguards */}
          <div style={{
            backgroundColor: '#f3e5f5',
            padding: '35px',
            borderRadius: '12px',
            border: '2px solid #9c27b0',
          }}
          itemScope
          itemType="https://schema.org/ItemList"
          >
            <h4 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              color: '#6a1b9a',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
            itemProp="name"
            >
              <span style={{ fontSize: '1.8rem' }} aria-hidden="true">🏢</span>
              Physical Safeguards
            </h4>
            <p style={{
              fontSize: '1rem',
              lineHeight: '1.7',
              color: '#555',
              marginBottom: '20px',
            }}>
              Layered physical security systems to control and monitor access:
            </p>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}>
              {safeguards.physical.map((item, index) => (
                <li key={index} style={{
                  fontSize: '0.95rem',
                  lineHeight: '1.9',
                  color: '#444',
                  paddingLeft: '25px',
                  position: 'relative',
                  marginBottom: '10px',
                }}
                itemProp="itemListElement"
                >
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: '#9c27b0',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                  }}
                  aria-hidden="true"
                  >✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Technical Safeguards */}
          <div style={{
            backgroundColor: '#e8f5e9',
            padding: '35px',
            borderRadius: '12px',
            border: '2px solid #4caf50',
          }}
          itemScope
          itemType="https://schema.org/ItemList"
          >
            <h4 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              color: '#2e7d32',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
            itemProp="name"
            >
              <span style={{ fontSize: '1.8rem' }} aria-hidden="true">🔒</span>
              Technical Safeguards
            </h4>
            <p style={{
              fontSize: '1rem',
              lineHeight: '1.7',
              color: '#555',
              marginBottom: '20px',
            }}>
              Advanced information security technology and protocols:
            </p>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}>
              {safeguards.technical.map((item, index) => (
                <li key={index} style={{
                  fontSize: '0.95rem',
                  lineHeight: '1.9',
                  color: '#444',
                  paddingLeft: '25px',
                  position: 'relative',
                  marginBottom: '10px',
                }}
                itemProp="itemListElement"
                >
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: '#4caf50',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                  }}
                  aria-hidden="true"
                  >✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Our Commitment Section */}
        <div style={{
          backgroundColor: '#1A1A1A',
          color: '#ffffff',
          padding: '50px 40px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        }}>
          <h3 style={{
            fontSize: '1.8rem',
            fontWeight: '700',
            marginBottom: '25px',
            color: '#00ffff',
          }}>
            Our Commitment to Security & Excellence
          </h3>
          <p style={{
            fontSize: '1.15rem',
            lineHeight: '1.9',
            maxWidth: '900px',
            margin: '0 auto',
          }}>
            Security is not just a compliance requirement for us — it's part of our culture. Every employee, system, and workflow is designed with one clear mission: to protect our clients' information and deliver reliable, high-quality outcomes every time. Our <strong style={{ color: '#00ffff' }}>revenue cycle management services for hospitals</strong> and clinics meet the highest industry standards, ensuring your practice operates with confidence and complete data protection.
          </p>

          {/* CTA Button */}
          <Link 
            to="/free-trial"
            style={{
              display: 'inline-block',
              marginTop: '30px',
              padding: '15px 35px',
              backgroundColor: '#F26522',
              color: '#ffffff',
              borderRadius: '30px',
              fontSize: '1.05rem',
              fontWeight: '700',
              textDecoration: 'none',
              boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#d3541c';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#F26522';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Experience Secure RCM - Start Free Trial
          </Link>
        </div>

        {/* Keywords Integration Footer */}
        <div style={{
          marginTop: '50px',
          padding: '30px',
          backgroundColor: '#ffffff',
          borderRadius: '10px',
          textAlign: 'center',
        }}>
          <p style={{
            fontSize: '1.05rem',
            lineHeight: '1.7',
            color: '#666',
            fontStyle: 'italic',
          }}>
            When you choose MDeRCM's <strong>healthcare revenue cycle management services</strong>, you're partnering with one of the <strong>best revenue cycle management companies</strong> that puts security, compliance, and quality at the forefront of everything we do.
          </p>
        </div>
      </div>
    </section>
  );
};

export default SecurityComplianceSection;